import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Steps from "../Steps/Steps";
import { BtnSize, BtnType, Button } from "../Button";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import {
  ALIGN_ITEMS,
  DISPLAY,
  JUSTIFY_CONTENT,
  SPACING,
} from "../../Constants/Enums/styles.enum";

interface Props {
  onStepChange: (step: number) => unknown;
  stepComponents: ReactNode[];
  submitText?: string;
  onSubmit: () => unknown;
  isSubmitLoading: boolean;
  stepLabels?: string[];
  stepTitles?: string[];
  stepDescriptions?: string[];
}

// Lets a parent force-advance to the next step from outside the normal
// "Next" button flow - e.g. a confirmation modal that lets the user
// proceed despite a warning raised by `onStepChange`.
export interface StepWrapperHandle {
  goToNextStep: () => void;
}

const StepWrapper = forwardRef<StepWrapperHandle, Props>(
  (
    {
      onStepChange,
      stepComponents,
      submitText = "Submit",
      onSubmit,
      isSubmitLoading = false,
      stepLabels = [],
      stepTitles = [],
      stepDescriptions = [],
    },
    ref,
  ) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isValidatingStep, setIsValidatingStep] = useState(false);
  const totalSteps = stepComponents.length;

  const containerRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement[]>([]);
  const [childHeight, setChildHeight] = useState<number[]>([]);

  const [transform, setTransform] = useState(0);

  const scrollToContainterTop = () => {
    if (containerRef && containerRef.current) {
      const yPos = containerRef.current.offsetTop;
      window.scrollTo({
        top: yPos,
        behavior: "smooth",
      });
    }
  };

  const advanceToNextStep = () => {
    const nextStep = currentStep + 1;

    if (nextStep <= totalSteps) {
      setTransform(-(100 * (nextStep - 1)));
      setCurrentStep(nextStep);
      scrollToContainterTop();
    }
  };

  const scrollToNextStep = async () => {
    // `onStepChange` may run an async check (e.g. an API call) before
    // deciding whether the step is allowed to advance, so its result is
    // always awaited - this is a no-op for the existing synchronous
    // (boolean-returning) validators.
    setIsValidatingStep(true);
    try {
      const canProceed = await onStepChange(currentStep);
      if (!canProceed) return;

      advanceToNextStep();
    } finally {
      setIsValidatingStep(false);
    }
  };

  useImperativeHandle(ref, () => ({
    goToNextStep: advanceToNextStep,
  }));

  const scrollToPrevoiusStep = () => {
    const previousStep = currentStep - 1;
    if (previousStep >= 1) {
      setTransform(-(100 * (previousStep - 1)));
      setCurrentStep(previousStep);
      scrollToContainterTop();
    }
  };

  useEffect(() => {
    if (
      childRef.current.length === stepComponents.length &&
      childHeight.length === 0
    ) {
      const tempHeightArray: number[] = [];

      childRef.current.forEach((child) => {
        if (child) {
          tempHeightArray.push(child.clientHeight);
        }
      });

      setChildHeight(tempHeightArray);
    }
  }, [stepComponents]);

  return (
    <div
      ref={containerRef}
      style={{
        backgroundColor: Colors.White,
        borderRadius: "16px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
      }}
    >
      <Steps
        totalSteps={stepComponents.length}
        currentStep={currentStep}
        stepLabels={stepLabels}
        stepTitles={stepTitles}
        stepDescriptions={stepDescriptions}
      />

      {/* Content Container */}
      <div
        style={{
          padding: "32px",
          backgroundColor: Colors.White,
        }}
      >
        <div
          style={{
            display: DISPLAY.FLEX,
            maxHeight: `${childHeight[currentStep - 1]}px`,
            overflowY: "auto",
            overflowX: "hidden",
            position: "relative",
          }}
        >
          {stepComponents.map((children, index) => (
            <div
              key={index}
              ref={(ref) => {
                childRef.current[index] = ref as HTMLDivElement;
              }}
              style={{
                minWidth: "100%",
                height: "fit-content",
                transform: `translateX(${transform}%)`,
                transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                opacity: index + 1 === currentStep ? 1 : 0.5,
              }}
            >
              {children}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div
        style={{
          display: DISPLAY.FLEX,
          justifyContent: JUSTIFY_CONTENT.SPACE_BETWEEN,
          alignItems: ALIGN_ITEMS.CENTER,
          padding: "24px 32px",
          borderTop: `1px solid ${Colors.LightGrey}`,
          backgroundColor: `${Colors.VeryLightGrey}50`,
        }}
      >
        {/* Step Counter */}
        <div
          style={{
            fontFamily: FontFamily.POPPINS_REGULAR,
            fontSize: "14px",
            color: Colors.Grey,
            fontWeight: 500,
          }}
        >
          Step {currentStep} of {totalSteps}
        </div>

        {/* Buttons */}
        <div
          style={{
            display: DISPLAY.FLEX,
            gap: SPACING.S12,
          }}
        >
          {currentStep > 1 && (
            <button
              onClick={scrollToPrevoiusStep}
              style={{
                padding: "10px 24px",
                backgroundColor: Colors.White,
                border: `2px solid ${Colors.LightGrey}`,
                borderRadius: "8px",
                cursor: "pointer",
                fontFamily: FontFamily.POPPINS_REGULAR,
                fontSize: "14px",
                color: Colors.Grey,
                fontWeight: "500",
                transition: "all 0.2s ease",
                display: DISPLAY.FLEX,
                alignItems: ALIGN_ITEMS.CENTER,
                gap: SPACING.S8,
                minWidth: "120px",
                justifyContent: JUSTIFY_CONTENT.CENTER,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = Colors.VeryLightGrey;
                e.currentTarget.style.borderColor = Colors.Grey;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = Colors.White;
                e.currentTarget.style.borderColor = Colors.LightGrey;
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z"
                  fill="currentColor"
                />
              </svg>
              Previous
            </button>
          )}

          {currentStep < totalSteps ? (
            <Button
              text="Next"
              btnType={BtnType.primary}
              btnSize={BtnSize.sm}
              onClick={scrollToNextStep}
              loading={isValidatingStep}
              btnStyle={{
                minWidth: "120px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              additionalComponent={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12l-4.58 4.59z"
                    fill="currentColor"
                  />
                </svg>
              }
            />
          ) : currentStep === totalSteps ? (
            <Button
              text={submitText}
              btnType={BtnType.primary}
              btnSize={BtnSize.sm}
              onClick={onSubmit}
              loading={isSubmitLoading}
              btnStyle={{
                minWidth: "140px",
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
  },
);

StepWrapper.displayName = "StepWrapper";

export default StepWrapper;
